import Button from '@mui/material/Button';
import type { ChipProps } from '@mui/material/Chip';
import type { SxProps, Theme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Iconify } from './iconify.js';

export const chipProps = { size: 'small', variant: 'soft' } satisfies Partial<ChipProps>;

export type FiltersBlockProps = {
  label: string;
  isShow: boolean;
  children: ReactNode;
};

/** One labelled group of removable chips, e.g. “Durum: Aktif”. */
export function FiltersBlock({ label, isShow, children }: FiltersBlockProps) {
  if (!isShow) return null;

  return (
    <BlockRoot>
      <BlockLabel>{label}</BlockLabel>
      <BlockContent>{children}</BlockContent>
    </BlockRoot>
  );
}

export type FiltersResultProps = {
  totalResults: number;
  onReset: () => void;
  children: ReactNode;
  sx?: SxProps<Theme>;
};

export function FiltersResult({ totalResults, onReset, children, sx }: FiltersResultProps) {
  const { t } = useTranslation();

  return (
    <ResultRoot sx={sx}>
      <ResultLabel>
        <strong>{totalResults}</strong>
        <span> {t('table.resultsFound')}</span>
      </ResultLabel>

      <ResultContent>
        {children}

        <Button
          color="error"
          onClick={onReset}
          startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
        >
          {t('common.action.clear')}
        </Button>
      </ResultContent>
    </ResultRoot>
  );
}

const ResultRoot = styled('div')``;

const ResultLabel = styled('div')(({ theme }) => ({
  ...theme.typography.body2,
  marginBottom: theme.spacing(1.5),
  '& span': { color: theme.palette.text.secondary },
}));

const ResultContent = styled('div')(({ theme }) => ({
  flexGrow: 1,
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const BlockRoot = styled('div')(({ theme }) => ({
  display: 'flex',
  overflow: 'hidden',
  gap: theme.spacing(1),
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  border: `dashed 1px ${theme.palette.divider}`,
  maxWidth: '100%',
}));

const BlockLabel = styled('span')(({ theme }) => ({
  height: 24,
  lineHeight: '24px',
  whiteSpace: 'nowrap',
  fontSize: theme.typography.subtitle2.fontSize,
  fontWeight: theme.typography.subtitle2.fontWeight,
}));

const BlockContent = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
}));
